import { hasOwn } from './domain.js';
import { countsInAggregates, countsInHeadcount, severanceTier } from './cost.js';
import { currentBucket, strandedPlanOf } from './decisions.js';
import { computeStrandedTimeline, computeTsaEconomics } from './economics.js';
import { previewPerimeterThreshold } from './analytics.js';
import { isPlacedInTarget } from './hierarchy.js';
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
const people = (n) => plural(n, 'person', 'people');
/** Up to five, then a marker — the same ceiling diagnostics uses, for the same reason. */
function some(parts) {
    return parts.slice(0, 5).join('; ') + (parts.length > 5 ? '; …' : '');
}
/*
  The checks, in the order a reader should meet them: everything misstated first, because those are
  the ones that make a number wrong, and a reader who stops after the first section should have
  stopped after the section that matters.
*/
const CHECKS = [
    /*
      An agreement recorded as over, with no month for when.
  
      `billedMonths` bills NOTHING in that case, and that is deliberate — it is the behaviour before
      the field existed, so a project imported from the frozen app is unchanged by the field's
      arrival. The consequence is what nobody sees: the register still lists the agreement with its
      fee, while the fee curve, the cumulative total and RemainCo's stranded income offset all read
      as though it had never been signed. Every one of those numbers is defensible on its own and
      the set of them is a contradiction.
    */
    {
        id: 'tsa-exited-undated',
        severity: 'misstated',
        run(state) {
            const undated = state.design.tsas.filter((t) => t.status === 'exited' && t.exitedAtMonth == null);
            if (!undated.length)
                return null;
            return {
                n: undated.length,
                title: `${undated.length} exited ${plural(undated.length, 'TSA')} with no exit month`,
                detail: `${some(undated.map((t) => `"${t.name}"`))}. An exit with no month bills nothing at all, ` +
                    `so the fee is in the register and in none of the economics — not the fee curve, not the ` +
                    `cumulative total, not the stranded income it was offsetting. Record the month it stopped, ` +
                    `or put the status back to agreed.`,
            };
        },
    },
    /*
      A Day-2 exit mark on a hire that is not in the carve-out.
  
      The mark means "this position is cut again at Day 2 rightsizing", which is a statement about the
      carve-out's shape; on a Remain hire it is read by nothing — the bridge gates on the bucket and
      `isDay2Exiter` refuses before it looks. So the panel shows a flag, the numbers ignore it, and
      the two never argue. The employee-side equivalent cannot survive (`bucketPatch` clears the meta
      when a person leaves the carve-out), which is precisely why this one is worth naming: it is the
      orphan the housekeeping does not reach.
    */
    {
        id: 'buildup-day2-orphan',
        severity: 'misstated',
        run(state) {
            const orphans = state.design.buildUps.filter((b) => b.day2Exit && currentBucket(state.design, b.id) !== 'Carve-out');
            if (!orphans.length)
                return null;
            return {
                n: orphans.length,
                title: `${orphans.length} planned ${plural(orphans.length, 'hire')} marked for Day-2 exit outside the carve-out`,
                detail: `${some(orphans.map((b) => `${b.name} (${currentBucket(state.design, b.id)})`))}. The mark only means anything to a carve-out position; here it is read by nothing, so ` +
                    `the panel shows a cut that no number takes. Move the hire to the carve-out, or clear the mark.`,
            };
        },
    },
    /*
      A stranded plan every engine throws away.
  
      TWO DOORS, and the check has to pass both or it under-reports. `strandedPlanOf` refuses a plan
      on anyone who is not a current, Remain-side, partly-dedicated person — but it deliberately
      ADMITS open positions and inactive people, because imported files carry plans against them and
      something has to be able to find them again. The engines that spend the plan do not: the
      timeline filters `countsInHeadcount` before it looks, and the one-off costs filter
      `countsInAggregates`. So a plan on an open requisition passes the eligibility door and is still
      read by nothing, which is the case a check written against eligibility alone would call fine.
  
      A plan can outlive its own eligibility, too — the person moves to the carve-out, is made
      redundant, leaves, or loses their dedication on a refresh — and the stored entry survives,
      because the design is a record of decisions and deleting one silently would be worse. What must
      not survive silently is the READING.
    */
    {
        id: 'stranded-plan-ignored',
        severity: 'misstated',
        run(state) {
            const design = state.design;
            const ignored = state.employees.flatMap((p) => {
                if (!hasOwn(design.strandedPlan, p.id))
                    return [];
                // The consumers' rule, not the door's: both filters must pass or no number moves.
                if (countsInHeadcount(p) && strandedPlanOf(design, p) !== null)
                    return [];
                return [{ p, why: whyUnread(state, p) }];
            });
            if (!ignored.length)
                return null;
            return {
                n: ignored.length,
                title: `${ignored.length} stranded ${plural(ignored.length, 'plan')} no engine reads`,
                detail: `${some(ignored.map(({ p, why }) => `${p.name} — ${why}`))}. The plan is still recorded ` +
                    `and it mitigates nothing: the stranded timeline, the one-off costs and the RemainCo pane ` +
                    `all pass over it. Clear the plan, or put back what made the person carry one.`,
            };
        },
    },
    /*
      A planned position nobody has put on a side.
  
      UNDECIDED, NOT MISSTATED, and the distinction was got wrong first time. `currentBucket` defaults
      to Remain and the hiring cost is booked to RemainCo, which is the right number for a position
      nobody has assigned — nothing disagrees with anything. What is true is that a hire created
      BECAUSE of the separation is, until somebody says otherwise, a RemainCo hire, and that is a
      decision worth taking on purpose. The proposal diff already says so for an agent-raised hire
      (`describeBatch` writes a "lands in:" row for exactly this trap); one created in the panel gets
      nothing, which is why it is worth a line here.
    */
    {
        id: 'buildup-undecided',
        severity: 'undecided',
        run(state) {
            const loose = state.design.buildUps.filter((b) => !hasOwn(state.design.buckets, b.id));
            if (!loose.length)
                return null;
            return {
                n: loose.length,
                title: `${loose.length} planned ${plural(loose.length, 'hire')} with no side of the separation`,
                detail: `${some(loose.map((b) => b.name))}. With no decision recorded they count as Remain, which ` +
                    `every engine reads consistently — so the numbers are right for a RemainCo hire. If any of ` +
                    `these was created for the carve-out, that is a decision nobody has taken yet.`,
            };
        },
    },
    /*
      An agreement with no replacement that lands in time.
  
      This is the register's central question and the one the economics already answer per agreement —
      `covered` means a FILLED, DATED replacement no later than the term's end. Each of the four ways
      to fail it is a different conversation (nobody has been asked to build it, the position is open,
      nobody has said when, or the date is after the cliff), so the reason travels with the name.
  
      EXITED AGREEMENTS ARE EXEMPT, exactly as both engine readings of the same field are: the
      register's `uncovered` tally and `tsaTimeline`'s per-row flag each gate on `status !== 'exited'`
      first. A service that came back in-house months ago needs no replacement in place by an exit
      that has already happened, and saying it does would put a red line on the one agreement somebody
      finished dealing with — while contradicting the two surfaces this check claims to defer to.
    */
    {
        id: 'tsa-uncovered',
        severity: 'undecided',
        run(state) {
            const uncovered = computeTsaEconomics(state).perTsa.filter((p) => p.tsa.status !== 'exited' && !p.covered);
            if (!uncovered.length)
                return null;
            const why = (p) => {
                if (!p.repl.length)
                    return 'nothing is linked to replace it';
                if (!p.replFilled.length)
                    return 'every linked replacement is still an open position';
                if (p.readyMonth === null)
                    return 'the replacement carries no needed-by month';
                const late = p.readyMonth - p.dur;
                return `the replacement lands M${p.readyMonth}, ${late} ${plural(late, 'month')} after the term ends`;
            };
            return {
                n: uncovered.length,
                title: `${uncovered.length} ${plural(uncovered.length, 'TSA')} with no replacement in place by the exit`,
                detail: `${some(uncovered.map((p) => `"${p.tsa.name}" — ${why(p)}`))}. Whoever loses the service ` +
                    `when the agreement ends has to be able to do it themselves by then; until each of these ` +
                    `has a filled, dated replacement, the exit date is an intention rather than a plan.`,
            };
        },
    },
    /*
      Stranded cost nobody has decided about.
  
      The residual is the headline of the whole RemainCo case, and it is a residual by DEFAULT —
      a carrier with no plan stays in it forever, so the steady-state number a committee is shown is
      partly a decision and partly an absence of one, and the pane cannot tell them apart. Naming the
      people converts "the residual is X" into "the residual is X, and here is who is still in it".
    */
    {
        id: 'stranded-unmitigated',
        severity: 'undecided',
        run(state) {
            const residual = computeStrandedTimeline(state).carriers.filter((c) => c.plan === null);
            if (!residual.length)
                return null;
            const worst = [...residual].sort((a, b) => b.d - a.d);
            return {
                n: residual.length,
                title: `${residual.length} stranded ${plural(residual.length, 'carrier')} with no mitigation plan`,
                detail: `${some(worst.map((c) => `${c.person.name} (${Math.round(c.d * 100)}% dedicated to the carve-out)`))}. Each one sits in the residual steady state by default rather than by decision. ` +
                    `Rightsize, redeploy, or say in the room that RemainCo is carrying them deliberately.`,
            };
        },
    },
    /*
      The stored perimeter rule disagrees with the perimeter.
  
      The rule sweeps the undecided and its own previous picks, never a human designation — that
      asymmetry is what makes it safe to drag around live. The commonest way to get here is the
      simplest: `setPerimeterRule` records a threshold WITHOUT moving anybody, because a rule and its
      application are deliberately two acts. After that the two can also drift on their own, as a
      dedication changes under the rule or a manual decision is released back to it.
  
      NOT A REFRESH, deliberately unsaid: a data update re-runs each scenario's own rule as part of
      the same act, so a refreshed roster is the one cause that cannot produce this. Naming it would
      send somebody to look at the last import for a discrepancy the import cannot have caused.
    */
    {
        id: 'perimeter-rule-stale',
        severity: 'undecided',
        run(state) {
            const threshold = state.design.perimeterThreshold;
            if (threshold == null)
                return null;
            const preview = previewPerimeterThreshold(state, threshold);
            const moved = preview.enterIds.length + preview.exitIds.length;
            if (!moved)
                return null;
            return {
                n: moved,
                title: `The perimeter rule would move ${moved} ${people(moved)} if it were applied now`,
                detail: `${preview.enterIds.length} would enter the perimeter and ${preview.exitIds.length} would ` +
                    `leave it. The stored rule is ${Math.round(threshold * 100)}% dedication and the perimeter ` +
                    `does not match it — either it was recorded without being applied, or a dedication has ` +
                    `changed under it. Apply the rule, or accept the perimeter as it stands and know it is now ` +
                    `a set of decisions rather than a policy.`,
            };
        },
    },
    /*
      Carve-out people with nowhere to go in the target.
  
      Silent unless a target design exists — the phase is opt-in, and before it is switched on
      "nobody is placed" is a description of a project that has not started, not a finding. After it
      is on, the unplaced are the gap between the population and the blueprint, and that gap is the
      whole point of the target view.
  
      PLANNED HIRES COUNT, because the target chart counts them: `getActivePeople` walks employees AND
      build-ups, and `buildHierarchy` drops whatever is not placed. A carve-out position created for
      the target org and never placed in it is therefore invisible in the very chart it was created
      for — the purest instance of the gap this check exists to name, and the one an employees-only
      walk would miss.
    */
    {
        id: 'target-unplaced',
        severity: 'undecided',
        run(state) {
            if (!state.targetEnabled)
                return null;
            const unplaced = [...state.employees, ...state.design.buildUps].filter((p) => countsInAggregates(p) &&
                currentBucket(state.design, p.id) === 'Carve-out' &&
                !isPlacedInTarget(state, p));
            if (!unplaced.length)
                return null;
            return {
                n: unplaced.length,
                title: `${unplaced.length} carve-out ${people(unplaced.length)} not placed in the target org`,
                detail: `${some(unplaced.map((p) => p.name))}. They are in the perimeter and in every Day-1 count, ` +
                    `and the target chart does not know where they sit — so it draws a smaller organisation ` +
                    `than the one transferring. Place them, or say the blueprint is partial.`,
            };
        },
    },
    /*
      Redundancies priced by the global default because no tier matched them.
  
      The severance chain is seniority, then country, then one flat default, and the default is a real
      number in the one-off total and in the pack. A population landing on it is not an error — a
      default is what it is for — but "40% of the people we are making redundant are priced at one
      flat figure nobody chose for them" is the single most common challenge a CFO makes to a
      severance envelope, and it is answerable in advance.
  
      THE POPULATION IS A MIRROR of `computeOneOffCosts`'s severance branches, deliberately not a
      re-derivation of "who might leave": the point is to describe the people whose severance is
      ACTUALLY IN THE TOTAL. A test ties the two counts together so the mirror cannot drift.
    */
    {
        id: 'severance-default',
        severity: 'undecided',
        run(state) {
            const design = state.design;
            const priced = state.employees.filter((p) => {
                if (!countsInAggregates(p))
                    return false;
                const bucket = currentBucket(design, p.id);
                if (bucket === 'No longer required')
                    return true;
                if (bucket === 'Carve-out')
                    return !!(design.bucketMeta[p.id] ?? {}).day2Exit;
                const plan = design.strandedPlan[p.id];
                return !!(plan && plan.action === 'rightsize' && p.dedication != null && p.dedication > 0);
            });
            const onDefault = priced.filter((p) => severanceTier(p, state.assumptions) === 'default');
            if (!onDefault.length)
                return null;
            const byCountry = new Map();
            for (const p of onDefault) {
                const key = String(p.country ?? '').trim() || '(no country)';
                byCountry.set(key, (byCountry.get(key) ?? 0) + 1);
            }
            const months = Number(state.assumptions.severanceMonthsDefault) || 0;
            return {
                n: onDefault.length,
                // The verb agrees with the SUBJECT — the count on the default — not with the denominator.
                title: `${onDefault.length} of ${priced.length} ${people(priced.length)} facing redundancy ` +
                    `${onDefault.length === 1 ? 'is' : 'are'} priced at the default severance`,
                detail: `${some([...byCountry.entries()].sort((a, b) => b[1] - a[1]).map(([c, n]) => `${c} (${n})`))}. ` +
                    `Neither their seniority nor their country carries a tier, so all of them inherit the ` +
                    `${months}-month default — one figure, across every grade and jurisdiction in that list. ` +
                    `State the tiers that apply in Assumptions, or be ready to say why one number is enough.`,
            };
        },
    },
];
/** Every check's id, in the order `reviewFindings` reports them. */
export const REVIEW_CHECK_IDS = CHECKS.map((c) => c.id);
/**
 * Everything wrong with, or undecided about, this design.
 *
 * PURE, AND A FUNCTION OF THE STATE IT IS GIVEN. That is the whole access story: on the platform a
 * caller's state has already been through `narrowState`, so a member who sees part of the
 * organisation gets a review of the part they see, computed from what they can already read, with
 * nothing to re-narrow afterwards. Every other design for this — a stored finding, a shared
 * summary, a nightly digest — has to answer "whose numbers are these" as a new question. This one
 * inherits the answer from the door.
 *
 * That inheritance is only as good as the narrowing, which is worth stating because building this
 * found a hole in it: `narrowDesign` dropped the bucket of every planned hire it kept, so a scoped
 * member's carve-out hires all read as Remain — in their bridge, in their costs, and here as a
 * finding about a decision somebody had in fact taken. The review did not cause that and does not
 * work around it; it is fixed at the door, and `scope.test.ts` holds it there.
 */
export function reviewFindings(state) {
    const found = [];
    for (const check of CHECKS) {
        const hit = check.run(state);
        if (hit)
            found.push({ id: check.id, severity: check.severity, ...hit });
    }
    return found;
}
/**
 * Why nothing reads this person's stranded plan, in the words the reader needs to act.
 *
 * IN THE ORDER THE DOORS ARE PASSED THROUGH, so the reason given is the reason that fired:
 * `strandedEligible`'s own sequence first, then the consumers' `countsInHeadcount`, which is the
 * one an eligibility-shaped answer would get wrong — an open requisition IS eligible to carry a
 * plan and is still skipped by every engine that would spend it.
 */
function whyUnread(state, p) {
    if (p.isLeaver)
        return 'has left the organisation';
    const bucket = currentBucket(state.design, p.id);
    if (bucket === 'Carve-out')
        return 'is in the carve-out, so strands nothing';
    if (bucket === 'No longer required')
        return 'is already no longer required';
    if (p.dedication == null)
        return 'has no dedication recorded';
    if (!(p.dedication > 0))
        return 'is 0% dedicated to the carve-out';
    if (p.openPosition)
        return 'is an open position, which the engines skip';
    if (p.inactive)
        return 'is marked inactive, which the engines skip';
    return 'has an entry naming no action';
}
//# sourceMappingURL=review.js.map